import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/it';

dayjs.extend(isSameOrBefore);
dayjs.locale('it');

const Calendar = ({ selectedDate, setSelectedDate, orders }) => {

  const selectedDateObj = dayjs(selectedDate);
  const todayObj = dayjs();

  const [currentMonth, setCurrentMonth] = useState(() =>
    selectedDateObj.startOf("month")
  );

  useEffect(() => {
    const newSel = dayjs(selectedDate);
    if (newSel.format("YYYY-MM") !== currentMonth.format("YYYY-MM")) {
      setCurrentMonth(newSel.startOf("month"));
    }
  }, [selectedDate]);

  const daysInMonth = currentMonth.daysInMonth();
  const firstDayOfMonth = currentMonth.startOf("month").day();

  const safeOrders = orders || {};

  const handleDayClick = (day) => {
    const clickedDate = currentMonth.date(day).format("YYYY-MM-DD");
    setSelectedDate(clickedDate);
  };

  const handleMonthChange = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = prev.add(direction, "month").startOf("month");
      setSelectedDate(newMonth.format("YYYY-MM-DD"));
      return newMonth;
    });
  };

  const ColorLegend = () => (
    <div style={{
      display: "flex", justifyContent: "center", gap: 20,
      padding: "8px 12px", background: "#f9fafb",
      borderRadius: 8, margin: "0 0 12px",
      flexWrap: "wrap",
    }}>
      <LegendItem color="#fef3c7" border="#fcd34d" text="Giorno selezionato" />
      <LegendItem color="#ffedd5" border="#fdba74" text="Ordini mancanti" />
    </div>
  );

  const LegendItem = ({ color, border, text }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{
        width: 12, height: 12, borderRadius: "50%",
        background: color, border: `1.5px solid ${border}`,
        flexShrink: 0,
      }} />
      <span style={{ fontSize: 11, color: "#6b7280" }}>{text}</span>
    </div>
  );

  return (
    <div style={{ padding: 16, background: "#fff", borderRadius: 12 }}>

      {/* Header navigazione mese */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <button
          onClick={() => handleMonthChange(-1)}
          style={{
            padding: "7px 16px", borderRadius: 8,
            border: "1px solid #e5e7eb", background: "#f9fafb",
            fontSize: 13, fontWeight: 500, color: "#374151",
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Indietro
        </button>

        <h2 style={{
          fontSize: 16, fontWeight: 600, color: "#111827",
          textTransform: "capitalize", margin: 0,
        }}>
          {currentMonth.format("MMMM YYYY")}
        </h2>

        <button
          onClick={() => handleMonthChange(1)}
          style={{
            padding: "7px 16px", borderRadius: 8,
            border: "1px solid #e5e7eb", background: "#f9fafb",
            fontSize: 13, fontWeight: 500, color: "#374151",
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Avanti
        </button>
      </div>

      {/* Legenda */}
      <ColorLegend />

      {/* Griglia giorni */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>

        {/* Intestazioni giorni settimana */}
        {["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"].map((d) => (
          <div key={d} style={{
            textAlign: "center", fontSize: 11, fontWeight: 600,
            color: "#9ca3af", padding: "4px 0", letterSpacing: "0.04em",
          }}>
            {d}
          </div>
        ))}

        {/* Celle vuote per allineare il primo giorno */}
        {[...Array(firstDayOfMonth).keys()].map((_, index) => (
          <div key={`empty-${index}`} />
        ))}

        {/* Giorni del mese */}
        {[...Array(daysInMonth).keys()].map((_, index) => {
          const day = index + 1;
          const dateObj = currentMonth.date(day);
          const formattedDate = dateObj.format("YYYY-MM-DD");

          const isSelected  = dateObj.isSame(dayjs(selectedDate), "day");
          const isToday     = dateObj.isSame(todayObj, "day");
          const isPassedDay = dateObj.isSameOrBefore(todayObj, "day") && !isToday;
          const isFuture    = dateObj.isAfter(todayObj, "day");

          // ✅ hasOrders: true se esiste almeno un ordine per quel giorno
          const hasOrders = safeOrders[formattedDate] && safeOrders[formattedDate].length > 0;

          // Calcola stile del giorno
          let bg = "#fff";
          let color = "#374151";
          let border = "1px solid transparent";
          let fontWeight = 400;
          let opacity = 1;

          if (isSelected) {
            // Giorno selezionato → giallo
            bg = "#fbbf24";
            color = "#111827";
            border = "1px solid #f59e0b";
            fontWeight = 700;
          } else if (isToday) {
            // Oggi → bordo viola
            bg = "#fff";
            color = "#4f46e5";
            border = "2px solid #4f46e5";
            fontWeight = 700;
          } else if (isPassedDay) {
            if (hasOrders) {
              // ✅ Giorno passato CON ordini → bianco normale
              bg = "#fff";
              color = "#6b7280";
              border = "1px solid #f3f4f6";
            } else {
              // ✅ Giorno passato SENZA ordini → arancione per segnalare che mancano
              bg = "#ffedd5";
              color = "#c2410c";
              border = "1px solid #fdba74";
              fontWeight = 600;
            }
          } else if (isFuture) {
            // Giorni futuri → grigio chiaro
            bg = "#fff";
            color = "#d1d5db";
            opacity = 0.7;
          }

          return (
            <button
              key={formattedDate}
              onClick={() => handleDayClick(day)}
              style={{
                background: bg,
                color,
                border,
                fontWeight,
                opacity,
                borderRadius: 8,
                padding: "8px 4px",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.15s",
                WebkitTapHighlightColor: "transparent",
                textAlign: "center",
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.background = isPassedDay && !hasOrders ? "#fed7aa" : "#f3f4f6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = bg;
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Calendar;